// /loop/:id — a loop's shareable card page.
import { RateSong } from "../ui/Stars";
import { useEffect, useState } from "react";
import {
  attribution,
  clockFor,
  isBpm,
  KITS,
  keyName,
  songKey,
  type Bpm,
  type LoopData,
  type PeriodDef,
} from "@eb/shared";
import { api } from "../api";
import { absoluteUrl, Link, useTitle } from "../lib/router";
import { MiniNotes } from "../river/MiniNotes";
import { fetchServerTime } from "../river/player";
import { formatPlayTime } from "../river/slots";
import { WizardShareBar } from "../ui/WizardShare";
import { riverPlayer, useRiverPlayer } from "../river/context";
import { engineHost } from "../audio/host";
import { ReportDialog } from "../ui/ReportDialog";
import { AddToTapeButton } from "../tapes/AddToTape";
import { streamHref, STREAM_BPM } from "../stream";

interface LoopInfo {
  id: string;
  /** The song's stream: it plays (and previews) at its own tempo on either site. */
  bpm: Bpm;
  day: number;
  slot: number | null;
  kind: string;
  author: string | null;
  title: string | null;
  wizardShare: number;
  hidden: boolean;
  data: LoopData | null;
}

export function LoopPage({ id }: { id: string }) {
  const [loop, setLoop] = useState<LoopInfo | null>(null);
  const [period, setPeriod] = useState<PeriodDef | null>(null);
  const [epoch, setEpoch] = useState<number | null>(null);
  const [error, setError] = useState("");
  // The preview state lives in the player (the global stop button and leaving the page end it).
  const player = useRiverPlayer();
  const previewing = !!player?.previewing;
  const [reporting, setReporting] = useState(false);
  useTitle(loop ? (loop.title ?? `Song by ${loop.author ?? "former contributor"}`) : "Song");
  useEffect(() => {
    void (async () => {
      try {
        const l = await api<LoopInfo>("GET", `/api/loops/${encodeURIComponent(id)}`);
        if (!isBpm(l.bpm)) l.bpm = 125;
        setLoop(l);
        setPeriod(await api<PeriodDef>("GET", `/api/periods/${l.day}?bpm=${l.bpm}`));
        setEpoch((await fetchServerTime()).epochMs);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [id]);
  if (error)
    return (
      <main id="main" className="page narrow">
        <p role="alert">{error}</p>
      </main>
    );
  if (!loop)
    return (
      <main id="main" className="page narrow">
        <p className="muted">Loading…</p>
      </main>
    );
  const author = loop.author ?? (loop.kind === "filler" ? "Wizard" : "former contributor");
  return (
    <main id="main" className="page narrow">
      <article className="panel stack loop-card-page">
        <h1>{loop.title ?? `Loop by ${author}`}</h1>
        <p>
          by{" "}
          {loop.author && loop.kind === "human" ? (
            <Link href={`/u/${encodeURIComponent(loop.author)}`}>{author}</Link>
          ) : (
            <strong>{author}</strong>
          )}
        </p>
        {loop.hidden ? (
          <p role="status" data-testid="loop-hidden">
            This song is hidden after reports and doesn&apos;t play.
          </p>
        ) : (
          <>
            <MiniNotes
              loop={loop.data ?? undefined}
              width={560}
              height={110}
              label={`Notes of the song by ${author}`}
            />
            {loop.kind === "human" && <RateSong loopId={id} author={author} />}
          </>
        )}
        {period && (
          <p className="row">
            <span className="chip">{keyName(songKey(period.key, loop.data))}</span>
            <span className="chip">{KITS[period.kit].name}</span>
            <span className="chip">{KITS[period.kit].genre}</span>
            {loop.bpm !== 125 && <span className="chip">{loop.bpm} BPM</span>}
          </p>
        )}
        {epoch !== null && loop.slot !== null && (
          <p>
            Plays {formatPlayTime(clockFor(loop.bpm).slotStartMs(loop.day, loop.slot, epoch))} (your time)
            {loop.bpm !== STREAM_BPM && (
              <>
                {" "}
                on <a href={streamHref(loop.bpm, "/")}>The Infinite{loop.bpm === 72 ? " 72" : ""}</a>
              </>
            )}
          </p>
        )}
        <WizardShareBar share={loop.wizardShare} filler={loop.kind === "filler"} />
        <p className="small muted">
          Licence: <Link href="/legal/licence">CC BY 4.0</Link> — credit:{" "}
          <span data-testid="credit">
            {attribution({
              title: loop.title,
              author: loop.author,
              kind: loop.kind,
              url:
                loop.bpm === STREAM_BPM
                  ? absoluteUrl(`/loop/${loop.id}`)
                  : `${location.origin}${streamHref(loop.bpm, `/loop/${loop.id}`)}`,
            })}
          </span>
        </p>
        {reporting && <ReportDialog loopId={loop.id} onClose={() => setReporting(false)} />}
        {loop.kind === "human" && !loop.hidden && (
          <div>
            <button type="button" className="btn btn-ghost" onClick={() => setReporting(true)} data-testid="report">
              Report
            </button>
          </div>
        )}
        {loop.data && !loop.hidden && (
          <div className="row">
            <button
              type="button"
              className="btn"
              onClick={() => {
                // Unlock audio inside the click (before any await): Safari needs that.
                const host = engineHost();
                void (async () => {
                  const p = await riverPlayer();
                  if (p.previewing) await p.endPreview();
                  else await p.preview(loop.day, loop.data!, await host, loop.bpm);
                })();
              }}
              data-testid="song-preview"
            >
              {previewing ? "■ Stop" : "▶ Preview this song"}
            </button>
            <AddToTapeButton loopId={id} />
          </div>
        )}
      </article>
    </main>
  );
}
